import { isNil } from 'lodash-es'
import { injectable } from 'inversify'
import {
  isPaginationUrlFilterNullableRelationType,
  isPaginationUrlFilterScalarValueType,
} from '@/services/tables/types'
import { FILTER_RESERVED_NAMES } from '@/services/tables/filters/types'
import { isRecordUnknown } from '@/utils/typeGuards'
import { customFieldFilterNamePrefix } from '@/utils/custom-fields/customFieldsFilter'
import type {
  CustomFieldPaginationUrlFilterNullableSingleType,
  CustomFieldPaginationUrlFilterScalarValueType,
  CustomFieldPaginationUrlFilterSingleType,
} from '@/utils/custom-fields/types'

@injectable()
export class FilterCleanupService {
  public cleanup(
    filters: CustomFieldPaginationUrlFilterNullableSingleType[],
  ): CustomFieldPaginationUrlFilterSingleType[] {
    return filters.map((f) => this.cleanupSingleFilter(f))
  }

  public cleanupSingleFilter(
    filter: CustomFieldPaginationUrlFilterNullableSingleType,
  ): CustomFieldPaginationUrlFilterSingleType {
    const result: CustomFieldPaginationUrlFilterSingleType = {}

    Object.entries(filter).forEach(([filterName, value]) => {
      // Delete reserved field names because it cannot be filters
      if (FILTER_RESERVED_NAMES.includes(filterName)) {
        return
      }
      if (filterName === customFieldFilterNamePrefix) {
        const customFilters = value as CustomFieldPaginationUrlFilterScalarValueType[]
        const cleanedCustomFilters = this.cleanup(customFilters) as CustomFieldPaginationUrlFilterScalarValueType[]
        result[customFieldFilterNamePrefix] = cleanedCustomFilters
      } else if (isPaginationUrlFilterNullableRelationType({ [filterName]: value })) {
        Object.entries(value).forEach(([relation, scalar]) => {
          if (!this.isEmptyValue(scalar)) {
            Object.entries(scalar as Array<unknown> | Record<string, unknown>).forEach(([operation, scalarValue]) => {
              if (!this.isEmptyValue(scalarValue)) {
                if (!isRecordUnknown(result[filterName])) {
                  result[filterName] = {}
                  result[filterName][relation] = {}
                }

                if (!isRecordUnknown(result[filterName][relation])) {
                  result[filterName][relation] = {}
                }

                result[filterName][relation][operation] = scalarValue
              }
            })
          }
        })
      } else if (isPaginationUrlFilterScalarValueType(value)) {
        Object.entries(value).forEach(([operation, scalarValue]) => {
          if (!this.isEmptyValue(scalarValue)) {
            if (!isRecordUnknown(result[filterName])) {
              result[filterName] = {}
            }

            result[filterName][operation] = scalarValue
          }
        })
      } else if (value !== null) {
        result[filterName] = value
      }
    })

    return result
  }

  protected isEmptyValue(value: unknown): boolean {
    return isNil(value) || (Array.isArray(value) && value.length === 0)
  }
}
